"use client";

import React, {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocalStorage } from "@uidotdev/usehooks";
import { shallowEqual } from "react-redux";

import { useSchoolLevelsOptional } from "@/providers/maps/contexts/school-levels-context";
import { areLayersEqual } from "@/providers/maps/utils/are-layers-equal";
import { buildInitialLayers } from "@/providers/maps/utils/build-initial-layers";
import { useAppSelector } from "@/providers/store/hooks";

import { filterSavedVisibility } from "../utils/filter-saved-visibility-by-available-types";

import type maplibregl from "maplibre-gl";

export type LayerKey = string;

export type LayersConfig = Record<
  LayerKey,
  {
    defaultFilter?: maplibregl.FilterSpecification;
    defaultHidden?: boolean;
    defaultHiddenTypes?: string[];
    visibilityTypeKey?: string;
  }
>;

export type SavedLayerVisibility = Record<
  LayerKey,
  {
    hidden?: boolean;
    hiddenTypes?: string[];
  }
>;

export interface HideAllLayersOptions {
  except?: LayerKey[];
  /** @deprecated No longer has special behavior; layers are always fully hidden. */
  resetHiddenTypesOnly?: LayerKey[];
  /** Applied in the same update as the hide (avoids races with sync-from-storage). */
  setVisibility?: Record<LayerKey, { hidden: boolean }>;
}

interface HideAllLayersVisibilityPatch {
  hidden: boolean;
  hiddenTypes: string[];
}

const MAP_VISIBILITY_STORAGE_KEY = "mapVisibility";

interface LayerState {
  filter?: maplibregl.FilterSpecification;
  hidden: boolean;
  hiddenTypes: string[];
}

interface MapLayersContextValue {
  layerKeys: LayerKey[];
  layers: Record<LayerKey, LayerState>;
  layerFilters: Record<LayerKey, maplibregl.FilterSpecification | undefined>;
  layerVisibility: Record<LayerKey, boolean>;
  layerHiddenTypes: Record<LayerKey, string[]>;
  savedLayerVisibility: Record<string, SavedLayerVisibility> | null;

  setLayerFilter: (
    key: LayerKey,
    filter: maplibregl.FilterSpecification | undefined,
  ) => void;
  setLayerHidden: (key: LayerKey, hidden: boolean) => void;
  setLayerHiddenTypes: (key: LayerKey, hiddenTypes: string[]) => void;
  toggleLayerHiddenType: (key: LayerKey, type: string) => void;
  getLayerFilter: (key: LayerKey) => maplibregl.FilterSpecification | undefined;
  getLayerHidden: (key: LayerKey) => boolean;
  getLayerHiddenTypes: (key: LayerKey) => string[];
  setSavedLayerVisibility: Dispatch<
    SetStateAction<Record<string, SavedLayerVisibility> | null>
  >;
  setStoredForPreset: (patch: Record<string, SavedLayerVisibility>) => void;
  hideAllLayers: (options?: HideAllLayersOptions) => void;
}

const MapLayersContext = createContext<MapLayersContextValue | null>(null);

export function useMapLayers() {
  const ctx = useContext(MapLayersContext);
  if (!ctx)
    throw new Error("useMapLayers must be used inside <MapLayersProvider />");
  return ctx;
}

interface MapLayersProviderProps {
  children: React.ReactNode;
  layersConfig: LayersConfig;
  /** When false, visibility is in-memory only (no mapVisibility localStorage). */
  persistVisibility?: boolean;
}

export function MapLayersProvider({
  children,
  layersConfig,
  persistVisibility = true,
}: MapLayersProviderProps) {
  const selectedDistrictId = useAppSelector(
    (s) => s.district.selectedDistrict?.id ?? null,
    shallowEqual,
  );

  const [persistentSavedLayerVisibility, setPersistentSavedLayerVisibility] =
    useLocalStorage<Record<string, SavedLayerVisibility> | null>(
      MAP_VISIBILITY_STORAGE_KEY,
      null,
    );

  const savedLayerVisibility = persistVisibility
    ? persistentSavedLayerVisibility
    : null;

  const setSavedLayerVisibility = useCallback<
    Dispatch<SetStateAction<Record<string, SavedLayerVisibility> | null>>
  >(
    (updater) => {
      if (!persistVisibility) return;
      setPersistentSavedLayerVisibility(updater);
    },
    [persistVisibility, setPersistentSavedLayerVisibility],
  );

  const currentDistrictVisibility = useMemo(() => {
    if (!persistVisibility || !selectedDistrictId || !savedLayerVisibility)
      return null;
    return savedLayerVisibility[String(selectedDistrictId)] ?? null;
  }, [persistVisibility, selectedDistrictId, savedLayerVisibility]);

  const [layers, setLayers] = useState<Record<LayerKey, LayerState>>(() =>
    buildInitialLayers(layersConfig, null, currentDistrictVisibility),
  );

  const layerKeys = useMemo(() => Object.keys(layersConfig), [layersConfig]);

  const districtKey =
    selectedDistrictId == null ? null : String(selectedDistrictId);

  const keysSig = useMemo(
    () =>
      Object.keys(layersConfig)
        .sort((a, b) => a.localeCompare(b))
        .join("|"),
    [layersConfig],
  );

  const prevDistrictKeyRef = useRef<string | null>(districtKey);
  const prevKeysSigRef = useRef<string>(keysSig);
  const prevSavedSigRef = useRef<string>("null");

  const { fulfilledTimeStamp, prevFulfilledTimeStamp, schoolLevels } =
    useSchoolLevelsOptional();

  useEffect(() => {
    if (!persistVisibility) return;

    const dataWasRefetched =
      Boolean(fulfilledTimeStamp) &&
      (!prevFulfilledTimeStamp ||
        prevFulfilledTimeStamp !== fulfilledTimeStamp);

    if (!currentDistrictVisibility || !dataWasRefetched) return;

    const availableTypes = new Set(schoolLevels?.map((l) => l.type) ?? []);

    const filteredVisibility = filterSavedVisibility(
      currentDistrictVisibility,
      availableTypes,
      layersConfig,
    );

    if (
      shallowEqual(currentDistrictVisibility, filteredVisibility) ||
      !filteredVisibility
    )
      return;

    setSavedLayerVisibility((prev) => {
      const next = { ...(prev ?? {}) };
      const districtKeyStr = String(selectedDistrictId);
      next[districtKeyStr] = filteredVisibility ?? {};

      if (Object.keys(next[districtKeyStr]).length === 0)
        next[districtKeyStr] = {};

      return Object.keys(next).length ? next : null;
    });
  }, [
    currentDistrictVisibility,
    fulfilledTimeStamp,
    layersConfig,
    persistVisibility,
    prevFulfilledTimeStamp,
    schoolLevels,
    selectedDistrictId,
    setSavedLayerVisibility,
  ]);

  useEffect(() => {
    const prevDistrictKey = prevDistrictKeyRef.current;
    const prevKeysSig = prevKeysSigRef.current;
    const prevSavedSig = prevSavedSigRef.current;

    const districtChanged = prevDistrictKey !== districtKey;
    const keysChanged = prevKeysSig !== keysSig;
    const dataWasRefetched =
      Boolean(fulfilledTimeStamp) &&
      (!prevFulfilledTimeStamp ||
        prevFulfilledTimeStamp !== fulfilledTimeStamp);

    const savedVisibilityForDistrict =
      districtKey && savedLayerVisibility
        ? (savedLayerVisibility[districtKey] ?? null)
        : null;
    const savedSig = JSON.stringify({
      visibility: savedVisibilityForDistrict ?? null,
    });
    const savedChanged = prevSavedSig !== savedSig;

    const shouldSyncFromStorage =
      districtChanged || dataWasRefetched || savedChanged;

    if (!districtChanged && !keysChanged && !savedChanged && !dataWasRefetched)
      return;

    prevDistrictKeyRef.current = districtKey;
    prevKeysSigRef.current = keysSig;
    prevSavedSigRef.current = savedSig;

    if (shouldSyncFromStorage) {
      if (!persistVisibility) {
        if (districtChanged || keysChanged) {
          const resetLayers = buildInitialLayers(layersConfig, null, null);
          setLayers((prev) =>
            areLayersEqual(prev, resetLayers) ? prev : resetLayers,
          );
        }

        return;
      }

      const nextLayers = buildInitialLayers(
        layersConfig,
        null,
        savedVisibilityForDistrict,
      );
      setLayers((prev) =>
        areLayersEqual(prev, nextLayers) ? prev : nextLayers,
      );

      return;
    }

    if (keysChanged) {
      setLayers((prev) => {
        const next = Object.keys(layersConfig).reduce<
          Record<LayerKey, LayerState>
        >((acc, key) => {
          if (prev[key]) {
            acc[key] = prev[key];
          } else {
            acc[key] = buildInitialLayers(
              { [key]: layersConfig[key] },
              null,
              savedVisibilityForDistrict
                ? { [key]: savedVisibilityForDistrict[key] }
                : null,
            )[key];
          }
          return acc;
        }, {});

        const changed = Object.keys(layersConfig).some((key) => !prev[key]);
        if (!changed && Object.keys(prev).length === Object.keys(next).length) {
          return prev;
        }

        return next;
      });
    }
  }, [
    districtKey,
    keysSig,
    layersConfig,
    persistVisibility,
    savedLayerVisibility,
    fulfilledTimeStamp,
    prevFulfilledTimeStamp,
  ]);

  const setLayerFilter = useCallback(
    (key: LayerKey, filter: maplibregl.FilterSpecification | undefined) => {
      if (!selectedDistrictId) return;

      setLayers((prev) => {
        if (!prev[key]) return prev;
        return { ...prev, [key]: { ...prev[key], filter } };
      });
    },
    [selectedDistrictId],
  );

  const setLayerHidden = useCallback(
    (key: LayerKey, hidden: boolean) => {
      setLayers((prev) => {
        if (!prev[key]) return prev;
        return { ...prev, [key]: { ...prev[key], hidden } };
      });

      if (!selectedDistrictId) return;

      setSavedLayerVisibility((prev) => {
        const next = { ...(prev ?? {}) };
        const districtKeyStr = String(selectedDistrictId);
        if (!next[districtKeyStr]) next[districtKeyStr] = {};
        const prevLayer = next[districtKeyStr][key] ?? {};
        next[districtKeyStr][key] = { ...prevLayer, hidden };

        if (Object.keys(next[districtKeyStr]).length === 0)
          next[districtKeyStr] = {};

        return Object.keys(next).length ? next : null;
      });
    },
    [selectedDistrictId, setSavedLayerVisibility],
  );

  const setLayerHiddenTypes = useCallback(
    (key: LayerKey, hiddenTypes: string[]) => {
      if (!selectedDistrictId) return;

      setLayers((prev) => {
        if (!prev[key]) return prev;

        return { ...prev, [key]: { ...prev[key], hiddenTypes } };
      });

      setSavedLayerVisibility((prev) => {
        const next = { ...(prev ?? {}) };
        const districtKeyStr = String(selectedDistrictId);
        if (!next[districtKeyStr]) next[districtKeyStr] = {};

        const prevLayer = next[districtKeyStr][key] ?? {};
        const nextLayer = { ...prevLayer };
        nextLayer.hiddenTypes = hiddenTypes;

        next[districtKeyStr][key] = nextLayer;

        if (Object.keys(next[districtKeyStr]).length === 0)
          next[districtKeyStr] = {};

        return Object.keys(next).length ? next : null;
      });
    },
    [selectedDistrictId, setSavedLayerVisibility],
  );

  const toggleLayerHiddenType = useCallback(
    (key: LayerKey, type: string) => {
      const currentLayer = layers[key];
      if (!currentLayer) return;

      const set = new Set(currentLayer.hiddenTypes);
      if (set.has(type)) set.delete(type);
      else set.add(type);
      const nextHiddenTypes = [...set];

      setLayers((prev) => {
        if (!prev[key]) return prev;
        return {
          ...prev,
          [key]: { ...prev[key], hiddenTypes: nextHiddenTypes },
        };
      });

      if (!selectedDistrictId) return;

      setSavedLayerVisibility((storedPrev) => {
        const next = { ...(storedPrev ?? {}) };
        const districtKeyStr = String(selectedDistrictId);
        if (!next[districtKeyStr]) next[districtKeyStr] = {};

        const prevLayer = next[districtKeyStr][key] ?? {};
        const nextLayer = { ...prevLayer };
        nextLayer.hiddenTypes = nextHiddenTypes;

        next[districtKeyStr][key] = nextLayer;

        if (Object.keys(next[districtKeyStr]).length === 0)
          next[districtKeyStr] = {};

        return Object.keys(next).length ? next : null;
      });
    },
    [layers, selectedDistrictId, setSavedLayerVisibility],
  );

  const layerFilters = useMemo(
    () =>
      Object.keys(layers).reduce<
        Record<LayerKey, maplibregl.FilterSpecification | undefined>
      >((acc, key) => {
        acc[key] = layers[key].filter;
        return acc;
      }, {}),
    [layers],
  );

  const layerVisibility = useMemo(
    () =>
      Object.keys(layers).reduce<Record<LayerKey, boolean>>((acc, key) => {
        acc[key] = !layers[key].hidden;
        return acc;
      }, {}),
    [layers],
  );

  const layerHiddenTypes = useMemo(
    () =>
      Object.keys(layers).reduce<Record<LayerKey, string[]>>((acc, key) => {
        acc[key] = layers[key].hiddenTypes;
        return acc;
      }, {}),
    [layers],
  );

  const getLayerFilter = useCallback(
    (key: LayerKey) => layerFilters[key],
    [layerFilters],
  );

  const getLayerHidden = useCallback(
    (key: LayerKey) => !layerVisibility[key],
    [layerVisibility],
  );

  const getLayerHiddenTypes = useCallback(
    (key: LayerKey) => layerHiddenTypes[key] ?? [],
    [layerHiddenTypes],
  );

  const setStoredForPreset = useCallback(
    (patch: Record<string, SavedLayerVisibility>) => {
      setSavedLayerVisibility((prev) => ({ ...(prev ?? {}), ...patch }));
    },
    [setSavedLayerVisibility],
  );

  const hideAllLayers = useCallback(
    ({ except, setVisibility }: HideAllLayersOptions = {}) => {
      if (!selectedDistrictId) return;

      const skip = new Set(except ?? []);
      const districtKeyStr = String(selectedDistrictId);
      const visibilityOverrides = setVisibility ?? {};

      const targetKeys = Object.keys(layersConfig).filter((k) => !skip.has(k));
      const updates: Record<
        LayerKey,
        Partial<HideAllLayersVisibilityPatch>
      > = {};

      targetKeys.forEach((key) => {
        const defaultHiddenTypes = layersConfig[key].defaultHiddenTypes ?? [];
        // Type-driven layers (school boundaries/pins) are controlled via hiddenTypes in the UI,
        // so hide them by filling hiddenTypes and keep parent `hidden` false.
        const isTypeDriven = layersConfig[key].visibilityTypeKey != null;
        updates[key] = {
          hidden: isTypeDriven ? false : true,
          hiddenTypes: defaultHiddenTypes,
        };
      });

      Object.entries(visibilityOverrides).forEach(([key, { hidden }]) => {
        if (layersConfig[key] == null) return;
        updates[key] = {
          ...(updates[key] ?? {}),
          hidden,
        };
      });

      const allKeys = Object.keys(updates);

      setLayers((prev) => {
        const next = { ...prev };

        const savedForDistrict =
          districtKey && savedLayerVisibility
            ? (savedLayerVisibility[districtKey] ?? null)
            : null;

        allKeys.forEach((key) => {
          const base =
            next[key] ??
            buildInitialLayers(
              { [key]: layersConfig[key] },
              null,
              savedForDistrict ? { [key]: savedForDistrict[key] } : null,
            )[key];

          next[key] = { ...base, ...updates[key] };
        });

        return next;
      });

      setSavedLayerVisibility((prev) => {
        const next = { ...(prev ?? {}) };
        const districtSlice = { ...(next[districtKeyStr] ?? {}) };

        allKeys.forEach((key) => {
          // Full replace for hide targets so sync-from-storage cannot revive stale visibility.
          if (targetKeys.includes(key)) {
            districtSlice[key] = {
              hidden: updates[key].hidden ?? true,
              hiddenTypes:
                updates[key].hiddenTypes ??
                layersConfig[key].defaultHiddenTypes ??
                [],
            };
            return;
          }

          districtSlice[key] = {
            ...(districtSlice[key] ?? {}),
            ...updates[key],
          };
        });

        next[districtKeyStr] = districtSlice;

        return Object.keys(next).length ? next : null;
      });
    },
    [
      districtKey,
      layersConfig,
      savedLayerVisibility,
      selectedDistrictId,
      setSavedLayerVisibility,
    ],
  );

  const value: MapLayersContextValue = {
    layerKeys,
    layers,
    layerFilters,
    layerVisibility,
    layerHiddenTypes,
    savedLayerVisibility,
    setLayerFilter,
    setLayerHidden,
    setLayerHiddenTypes,
    toggleLayerHiddenType,
    getLayerFilter,
    getLayerHidden,
    getLayerHiddenTypes,
    setSavedLayerVisibility,
    setStoredForPreset,
    hideAllLayers,
  };

  return (
    <MapLayersContext.Provider value={value}>
      {children}
    </MapLayersContext.Provider>
  );
}
